import {useSelector} from "react-redux";
import {useMemo} from "react";
import {isEmpty} from "es-toolkit/compat";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEllipsisVertical} from "@fortawesome/pro-regular-svg-icons/faEllipsisVertical";

import Button from "@/components/Global/Button/Button";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import Doughnut from "@/components/Global/Charts/Doughnut/Doughnut";
import {HeaderWithTabs} from "..";

import {checkIsDemoAccount} from "@/helpers/checkIsDemoAccount";
import {useOrders} from "@/hooks/useOrders/useOrders.js";
import {useResults} from "@/hooks/useResults/useResults.js";

import {config} from "@/config";

import styles from "./AllResultsHeader.module.scss";
import {selectUserDetails} from "@/features/user/userSelectors";

dayjs.extend(isSameOrAfter);

const DAYS_FROM_LATEST_TEST = 180;

export default function AllResultsHeader() {
  const customerId = useSelector(selectUserDetails)?.ID || 0;
  const isDemoAccount = checkIsDemoAccount(customerId);

  const {latestResult} = useResults();
  const {getNextSubscription} = useOrders();

  const isNewCustomer = isEmpty(latestResult);
  const nextSubscriptionTest = getNextSubscription();
  const isSubscriptionTest = !isEmpty(nextSubscriptionTest);

  const now = dayjs();
  let endDate = dayjs(latestResult?.orderCompleteDate).add(DAYS_FROM_LATEST_TEST, "day");
  let daysLeft = endDate.diff(now, "days");
  let frequencyInDays, latestSubscriptionDate;

  if (nextSubscriptionTest && !isEmpty(nextSubscriptionTest)) {
    endDate = dayjs(nextSubscriptionTest?.orderCompleteDate).add(DAYS_FROM_LATEST_TEST, "day");
    daysLeft = endDate.diff(now, "days");
  }

  if (isSubscriptionTest) {
    latestSubscriptionDate = dayjs(nextSubscriptionTest?.nextDispatch)
      .subtract(nextSubscriptionTest?.billingFrequency, "month")
      .format("YYYY-MM-DD");
    endDate = dayjs(nextSubscriptionTest?.nextDispatch);
    frequencyInDays = endDate.diff(latestSubscriptionDate, "days");
    daysLeft = endDate.diff(dayjs(), "days");
  }

  let percentageComplete = 100;
  if (daysLeft > 0) {
    percentageComplete = 100 - (daysLeft / DAYS_FROM_LATEST_TEST) * 100;

    if (isSubscriptionTest) {
      percentageComplete = 100 - (daysLeft / frequencyInDays) * 100;
    }
  }
  // const {isDesktop} = useMediaQuery();

  const showHeaderTab = useMemo(() => {
    return false;

    // if (isDesktop) return false;
    // return !isNewCustomer;
  }, []);

  const headerTabs = [
    {id: "latest-results", label: "Latest results", ariaLabel: "latest results", isActive: true, show: showHeaderTab},
    {id: "all-tests", label: "All tests", ariaLabel: "all tests", isActive: false, show: showHeaderTab},
  ];

  return (
    <HeaderWithTabs variant="tabs" className="py-1" tabs={headerTabs}>
      <div className={`d-flex px-2 align-items-center ${styles.header}`}>
        <div className="col py-4 py-md-0">
          <h1 className={`${styles.pageTitle} fw-medium mb-0`}>Results</h1>
        </div>

        <RenderIf isTrue={!isNewCustomer}>
          <div className="col-auto">
            <div className={"d-flex align-items-center justify-content-end"}>
              <div className="col-auto">
                <small className="fw-normal">Next test</small>
              </div>
              <div className="col">
                <Doughnut
                  score={percentageComplete}
                  customStyle={{
                    colorsRequired:
                      (config.websiteId == 1 || config.websiteId == 3) && !window.config.isWhiteLabelApp
                        ? ["black"]
                        : window.config.isWhiteLabelApp
                          ? ["--bs-header-result-doughnut"]
                          : ["orange"],
                    hasBlob: false,
                    hasGlow: false,
                    rotation: 0,
                    borderRadius: 0,
                    rootMargin: "0px 0px 0px 0px",
                    showBackground: false,
                  }}
                  style={{height: "100%", minWidth: "80px", maxHeight: "80px", aspectRatio: 1}}>
                  <RenderIf isTrue={daysLeft > 0}>
                    <h1 className={`${styles.doughnutTitle}`}>{daysLeft}</h1>
                    <h2 className={`${styles.doughnutSubtitle}`}>Days</h2>
                  </RenderIf>
                  <RenderIf isTrue={daysLeft <= 0}>
                    <h1 className={`${styles.doughnutToday} mb-0`}>Today</h1>
                  </RenderIf>
                </Doughnut>
              </div>
              <RenderIf isTrue={!isDemoAccount}>
                <div className="col-auto d-inline-block d-md-none">
                  <Button variant="transparent" href="/my-account" aria-label="go to my account">
                    <FontAwesomeIcon className={`${styles.iconSize}`} icon={faEllipsisVertical} size="lg" />
                  </Button>
                </div>
              </RenderIf>
            </div>
          </div>
        </RenderIf>
      </div>
    </HeaderWithTabs>
  );
}
